import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown/types";
import type { FacilitySelectorGroup } from "./utils";

/** Widest closed-state control; the menu opens wider (MENU_WIDTH) so long facility names fit. */
const SELECTOR_MAX_WIDTH = 280;
const MENU_WIDTH = 340;
const MAX_MENU_HEIGHT = 420;
/**
 * Upper bound on concurrently selected facilities. Raised 20 -> 50 to match the network cap
 * (`MULTI_FACILITY_MAX_DEPARTMENTS`), so selecting every facility in the largest real portfolio is
 * possible; the two must stay equal or "All" would request more than the endpoint accepts.
 */
const MAX_SELECTED_FACILITIES = 50;

/** Prefix marking a region heading row, so its synthetic value can never collide with a facility id. */
const REGION_ROW_PREFIX = "__region__:";

type Props = {
	/** Facilities bucketed by region, west-to-east, with legend-matching deduped labels. */
	groups: FacilitySelectorGroup[];
	/** Controlled selection — the container owns the state and every view derives its scope from it. */
	selectedIds: string[];
	onChange: (nextSelectedIds: string[]) => void;
	/** Size of the full capped facility set (the N in the compact "X of N" closed display). */
	totalCount: number;
};

/**
 * Page-level facility multi-select for the Multi-Facility Management Center: the single control over
 * which facilities the table, heatmap and charts show, purely client-side (selection never reaches a
 * query key — zero refetch).
 *
 * Selection changes are reported straight to the container, which owns the selection-changed
 * analytics for every path into it (this dropdown, a table/heatmap row's remove, and Clear all) —
 * previously only this component tracked, so the other two paths went unrecorded.
 *
 * Facilities sit under region headings derived from each department's timezone, following the
 * established grouped-dropdown pattern (`DepartmentJobsConfig`): a heading row carries `groupValues`,
 * so clicking a region selects or clears that whole region.
 *
 * The headings are deliberately NOT collapsible. Collapsing removes a region's rows from the option
 * list, and the panel's "All Facilities" / "Clear all" act on the VISIBLE options — so with every
 * region collapsed, "All" would select nothing. Grouping is what makes a long list scannable;
 * collapsing would trade that for a way to silently empty the selection.
 */
export const MultiFacilitySelector: FC<Props> = ({ groups, selectedIds, onChange, totalCount }) => {
	const { t } = useTranslation();

	// A heading row per region (clicking it toggles that whole region via `groupValues`), followed by
	// the region's facilities. A single-region network still gets its heading, which is what tells the
	// reader the list is regional rather than arbitrary.
	const items = useMemo<DropdownItem<string>[]>(() => {
		const rows: DropdownItem<string>[] = [];
		for (const group of groups) {
			rows.push({
				value: `${REGION_ROW_PREFIX}${group.label}`,
				label: `${group.label} (${group.departmentIds.length})`,
				groupValues: group.departmentIds,
			});
			rows.push(...group.items);
		}
		return rows;
	}, [groups]);

	// Compact closed state instead of the default chip row: "All Facilities" or "X of N" — the
	// floating label already says "Facilities", so the value itself stays word-free.
	const renderClosedValue = useCallback(
		() => (selectedIds.length === totalCount ? t("all.facilities") : t("mc.multi.selector.value.count", { selected: selectedIds.length, count: totalCount })),
		[selectedIds.length, totalCount, t],
	);

	return (
		<SystemDropdown<DropdownItem<string>>
			multiple
			items={items}
			selectedItems={selectedIds}
			onChangeMultiple={onChange}
			label={t("mc.multi.selector.label")}
			showFilter
			// Zero is allowed: deselecting every facility empties the views (each falls back to its own
			// "No Data" state), which is a deliberate way to blank the comparison.
			minimalSelection={0}
			maximalSelection={MAX_SELECTED_FACILITIES}
			// "All" is offered now that the selection cap equals the network cap, so selecting every
			// facility can no longer ask for more than the endpoint accepts.
			allOption={t("all.facilities")}
			renderValue={renderClosedValue}
			menuWidth={MENU_WIDTH}
			maxMenuHeight={MAX_MENU_HEIGHT}
			data-testid="mc-multi-facility-selector"
			formControlSx={{ width: { xs: "100%", sm: `${SELECTOR_MAX_WIDTH}px` } }}
		/>
	);
};
